import { useEffect, useState } from 'react';

import { cloneDeep, omit } from 'lodash';
import MuiPhoneNumber from 'material-ui-phone-number';
import { useTranslation } from 'react-i18next';

import { Contact } from './types';

import { ContactType } from 'RestAPIs/contacts/contacts';

import { TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';

import { Dialog, EmailField } from 'components/shared';

export type ProcessedContact = Omit<Contact, 'id' | 'type'> & {
  id?: number;
  type?: ContactType;
};

type Props = {
  contact?: Contact | null;
  type?: ContactType;
  onSave: (contact: ProcessedContact) => void;
  onHidden: () => void;
};

export default function ContactDialog({
  contact: editedContact,
  type,
  onSave,
  onHidden
}: Props) {
  const isEdit = Boolean(editedContact);
  const { t } = useTranslation('common');
  const [canSave, setCanSave] = useState(false);
  const [isOpen, setOpen] = useState(true);
  const [defaultContactType, setDefaultContactType] = useState<ContactType>();
  const contactTypes = [
    ContactType.APPLICATION_MANAGER,
    ContactType.APPLICATION_OWNER,
    ContactType.ROSTER_PROCESS_MANAGER,
    ContactType.FLEX_PROCESS_MANAGER,
    ContactType.ICT_MANAGER,
    ContactType.DATABASE_VIEWS_API_INTEGRATIONS,
    ContactType.EXPORT_SALARY_ADMINISTRATOR,
    ContactType.EXPORT_FINANCIAL,
    ContactType.SECURITY_OFFICER
  ];
  const [contact, setContact] = useState<ProcessedContact>(
    editedContact
      ? cloneDeep(editedContact)
      : {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          emailAddress: '',
          remark: '',
          type
        }
  );

  useEffect(() => {
    setDefaultContactType(contact.type);
  }, [contactTypes]);

  const updateContact = (
    key: keyof ProcessedContact,
    value: string | number
  ) => {
    const updatedContact = { ...contact, [key]: value };
    setContact(updatedContact);
    setCanSave(
      Object.values(omit(updatedContact, ['id', 'remark'])).every((v) => v)
    );
  };

  return (
    <Dialog
      title={isEdit ? t('editContact') : t('addContact')}
      open={isOpen}
      onHidden={onHidden}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            defaultValue={defaultContactType}
            options={contactTypes}
            renderInput={(params) => (
              <TextField {...params} label={t('contactType')} required />
            )}
            getOptionLabel={(option) => t(`contactTypes.${option}`)}
            disableClearable
            onChange={(e, value) => updateContact('type', value)}
            isOptionEqualToValue={(a, b) => a === b}
          />
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <TextField
            label={t('firstName')}
            value={contact.firstName}
            onChange={(e) => updateContact('firstName', e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={t('lastName')}
            value={contact.lastName}
            onChange={(e) => updateContact('lastName', e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6}>
          <MuiPhoneNumber
            label={t('phoneNumber')}
            defaultCountry="nl"
            regions="europe"
            value={contact.phoneNumber}
            onChange={(value) =>
              updateContact('phoneNumber', value.replace(/\s/g, ''))
            }
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6}>
          <EmailField
            value={contact.emailAddress}
            setEmailValue={(value) => updateContact('emailAddress', value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('remark')}
            value={contact.remark}
            onChange={(e) => updateContact('remark', e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
      </Grid>

      <DialogActions className="dialog-actions">
        <Button
          onClick={() => setOpen(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={() => {
            onSave(contact);
            setOpen(false);
          }}
          color="primary"
          variant="contained"
          disabled={!canSave}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
